import IndustryGrid from "@components/free-whatsapp-message-templates/IndustryUseCasesGrid";
import NewLayout from "@components/new-ui/Layout";
import TemplateHeader from "@components/free-whatsapp-message-templates/TemplateHeader";
import TemplatesGrid from "@components/free-whatsapp-message-templates/TemplatesGrid";
import React from "react";
import FeatureCard from "@components/free-whatsapp-message-templates/FeatureCard";
import { UseCase } from "../free-whatsapp-message-templates/index";
import { ContentItem } from "@components/free-whatsapp-message-templates/TemplatesGrid";
import { industryColorData } from "../../../types/wa-message-templates";
import { GetServerSideProps, GetServerSidePropsResult } from "next";
import { getJSON } from "@utils/fetch";

interface TemplatesIndexProps {
    templatesData: ContentItem[];
    useCase: string;
    industry: string;
    useCases: UseCase[];
}

const TemplatesIndex: React.FC<TemplatesIndexProps> = ({ templatesData, useCase, industry, useCases }) => {
    const ogImage = industryColorData[industry]["og-image"];
    return (
        <NewLayout
            title={`Free ${industry.toLowerCase()} WhatsApp message templates for ${useCase.toLowerCase()}`}
            description={`We have curated some engaging examples of the most frequently-used WhatsApp message templates for the ${useCase.toLowerCase()} use case for the ${industry.toLowerCase()} industry.`}
            ogAttributes={{ image: ogImage }}
            keywords="WhatsApp Chat button, WhatsApp Chat Widget, How to create chat widget for free"
        >
            <TemplateHeader
                chipTitle="WhatsApp message templates"
                mainTitle={`Free ${industry} industry WhatsApp message templates for ${useCase.toLowerCase().trim().replace("faqs", "FAQs")}`}
                description={`We have curated some engaging and sleek examples of the most frequently\u2011used WhatsApp message templates for the ${useCase
                    .toLowerCase()
                    .trim()
                    .replace("faqs", "FAQs")} use case for the ${industry.toLowerCase()} industry.`}
                simpleText="Automate your user conversations while you focus on the big picture"
                buttonText="Try Gallabox for Free"
                variantColor={industryColorData[industry]["header-variant"]}
                chipBg={industryColorData[industry]["chip-bg"]}
                chipBorder={industryColorData[industry]["chip-border"]}
                chipTextColor={industryColorData[industry]["chip-text"]}
                imageLink={industryColorData[industry]["image-link"]}
            />
            <TemplatesGrid contents={templatesData} />
            <IndustryGrid industry={industry} useCase={useCase} useCases={useCases} />
            <FeatureCard
                title="Access the business applications of WhatsApp message templates across industries"
                description="Check out the rest of the collection of WhatsApp business message templates for 9 more industries. Maybe, you could mix and match the templates for some of your business needs."
                linkText="Explore other industries"
                href="/resources/free-whatsapp-message-templates"
                image="https://assets.gallabox.com/gb-home/programmatic_seo/industries-image.png"
            />
            <div
                className="flex flex-col items-center gap-7 py-10"
                style={{
                    background: "linear-gradient(0deg, rgba(210, 233, 255, 0.80) 0%, rgba(210, 233, 255, 0.80) 100%)",
                }}
            >
                <style jsx>{`
                    @media (min-width: 1024px) {
                        /* This is the breakpoint for laptops (lg) */
                        div {
                            background: linear-gradient(92deg, rgba(210, 233, 255, 0.8) 0%, rgba(255, 241, 214, 0.8) 50.5%, rgba(214, 208, 255, 0.8) 100%) !important;
                        }
                    }
                `}</style>
                <p className="text-4xl font-semibold text-gray-700 text-center mx-5 lg:mx-44 lg:max-w-6xl w-full ">
                    Bring life to your WhatsApp chats with automations that engage, convert and boost your revenue.
                </p>
                <a
                    href="https://app.gallabox.com/signup"
                    target="_blank"
                    rel="noreferrer"
                    className="px-4 py-2 lg:px-8 text-white font-bold rounded-md hover:no-underline focus:outline-none hover:shadow-lg bg-blue-500 hover:bg-blue-600 transition duration-300 ease-in-out w-fit cursor-pointer"
                >
                    Get your 7-day free trial here
                </a>
            </div>
        </NewLayout>
    );
};

export const getServerSideProps: GetServerSideProps<TemplatesIndexProps> = async (context): Promise<GetServerSidePropsResult<TemplatesIndexProps>> => {
    const { slug } = context.params as { slug: string }; // Ensure context.params is typed
    let templatesData: ContentItem[] = [];
    let useCases: UseCase[] = [];
    let useCase = "";
    let industry = "";
    let pageId = "";

    try {
        const url = `/resources/free-whatsapp-message-templates/${slug}`;
        useCases = await getJSON("/rest/v1/use_cases");
        const data = await getJSON(`/rest/v1/use_cases?template_link=eq.${url}`);
        if (data && data.length > 0) {
            pageId = data[0].id;
            useCase = data[0].use_case;
            industry = data[0].industry;
            const templates = await getJSON(`/rest/v1/templates?case_id=eq.${pageId}`);
            templatesData = templates.map((item: { content: string }) => item.content);
        }
    } catch (err) {
        console.error("Error fetching data:", err);
    }

    return {
        props: {
            templatesData,
            useCase,
            industry,
            useCases,
        },
    };
};

export default TemplatesIndex;
